import { Controller } from "@hotwired/stimulus"
import Logger from '../lib/logger'

/**
 * File Preview Controller
 * 
 * A reusable Stimulus controller for file upload preview functionality.
 * Handles file validation, preview display, and form input management.
 * 
 * Usage:
 *   <div data-controller="file-preview"
 *        data-file-preview-max-size-value="10485760"
 *        data-file-preview-max-files-value="10">
 *     <input type="file" multiple data-file-preview-target="input"
 *            data-action="change->file-preview#handleSelect">
 *     <div class="d-none" data-file-preview-target="preview">
 *       <div data-file-preview-target="list"></div>
 *     </div>
 *   </div>
 */
export default class extends Controller {
  static targets = ["input", "preview", "list"]
  static values = {
    maxSize: { type: Number, default: 10485760 }, // 10MB
    maxFiles: { type: Number, default: 10 },
    allowedTypes: {
      type: Array,
      default: ["image/jpeg", "image/png", "image/gif", "image/webp",
        "application/pdf", "text/plain", "text/csv",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document"]
    }
  }

  connect() {
    this.files = []
  }

  // Trigger file input click
  trigger(event) {
    event.preventDefault()
    this.inputTarget.click()
  }

  // Handle file selection
  handleSelect(event) {
    const newFiles = Array.from(event.target.files)

    if (newFiles.length === 0) return

    const validFiles = this.validateFiles(newFiles)

    if (validFiles.length > 0) {
      this.files = [...this.files, ...validFiles]
      this.renderPreviews()
      this.showPreview()
      this.updateFormInput()
    }
  }

  // Handle file selection (alias for handleSelect used in some templates)
  handleFiles(event) {
    this.handleSelect(event)
  }

  // Validate files against size and type restrictions
  validateFiles(files) {
    const validated = []

    for (const file of files) {
      // Check max files limit
      if (this.files.length + validated.length >= this.maxFilesValue) {
        this.dispatchError(`Maximum ${this.maxFilesValue} files allowed`)
        break
      }

      // Check file size
      if (file.size > this.maxSizeValue) {
        this.dispatchError(`${file.name} exceeds maximum size of ${this.formatSize(this.maxSizeValue)}`)
        continue
      }

      // Check file type (if restrictions exist)
      if (this.allowedTypesValue.length > 0 && !this.isTypeAllowed(file.type)) {
        this.dispatchError(`${file.name} has an unsupported file type`)
        continue
      }

      validated.push(file)
    }

    return validated
  }

  // Check if file type is allowed
  isTypeAllowed(mimeType) {
    return this.allowedTypesValue.some(allowed => {
      if (allowed.endsWith("/*")) {
        // Wildcard match (e.g., "image/*")
        return mimeType.startsWith(allowed.slice(0, -1))
      }
      return mimeType === allowed
    })
  }

  // Render file previews
  renderPreviews() {
    if (!this.hasListTarget) return

    this.listTarget.innerHTML = this.files.map((file, index) =>
      this.createPreviewElement(file, index)
    ).join("")
  }

  // Create preview HTML for a file
  createPreviewElement(file, index) {
    const isImage = file.type.startsWith("image/")
    const iconClass = this.getFileIcon(file.type)

    return `
      <div class="file-preview-item d-flex align-items-center p-2 bg-light rounded me-2 mb-2" 
           data-index="${index}">
        ${isImage ? this.createImagePreview(file) : `<i class="${iconClass} me-2 text-muted"></i>`}
        <div class="flex-grow-1 min-width-0">
          <div class="text-truncate small fw-medium">${this.escapeHtml(file.name)}</div>
          <div class="text-muted smaller">${this.formatSize(file.size)}</div>
        </div>
        <button type="button" 
                class="btn btn-sm btn-outline-danger ms-2" 
                data-action="file-preview#remove"
                data-index="${index}"
                title="Remove file">
          <i class="fas fa-times"></i>
        </button>
      </div>
    `
  }

  // Create image preview thumbnail
  createImagePreview(file) {
    const objectUrl = URL.createObjectURL(file)
    return `<img src="${objectUrl}" 
                 alt="${this.escapeHtml(file.name)}" 
                 class="file-thumbnail me-2 rounded object-fit-cover"
                 width="40" height="40"
                 data-action="load->file-preview#revokeUrl">`
  }

  // Revoke Object URL to free memory
  revokeUrl(event) {
    URL.revokeObjectURL(event.target.src)
  }

  // Get appropriate icon for file type
  getFileIcon(mimeType) {
    const iconMap = {
      "application/pdf": "fas fa-file-pdf",
      "text/plain": "fas fa-file-alt",
      "text/csv": "fas fa-file-csv",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "fas fa-file-excel",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "fas fa-file-word"
    }

    if (mimeType.startsWith("image/")) return "fas fa-file-image"
    return iconMap[mimeType] || "fas fa-file"
  }

  // Remove a file from the list
  remove(event) {
    event.preventDefault()
    const index = parseInt(event.currentTarget.dataset.index)

    if (isNaN(index) || index < 0 || index >= this.files.length) return

    this.files.splice(index, 1)
    this.renderPreviews()
    this.updateFormInput()

    if (this.files.length === 0) {
      this.hidePreview()
      this.clearInput()
    }

    this.dispatch("removed", { detail: { remainingFiles: this.files.length } })
  }

  // Update the form file input with current files
  updateFormInput() {
    const dt = new DataTransfer()
    this.files.forEach(file => dt.items.add(file))
    this.inputTarget.files = dt.files

    this.dispatch("changed", { detail: { files: this.files } })
  }

  // Clear all files
  clear() {
    this.files = []
    this.clearInput()
    this.hidePreview()

    if (this.hasListTarget) {
      this.listTarget.innerHTML = ""
    }

    this.dispatch("cleared")
  }

  // Clear all files (alias for clear used by outlets)
  clearAll() {
    this.clear()
  }

  clearInput() {
    this.inputTarget.value = ""
  }

  showPreview() {
    if (this.hasPreviewTarget) {
      this.previewTarget.classList.remove("d-none")
    }
  }

  hidePreview() {
    if (this.hasPreviewTarget) {
      this.previewTarget.classList.add("d-none")
    }
  }

  // Dispatch error event
  dispatchError(message) {
    this.dispatch("error", { detail: { message } })
    Logger.warn("File preview error:", message)
  }

  // Format file size for display
  formatSize(bytes) {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / 1048576).toFixed(1)} MB`
  }

  // Escape HTML to prevent XSS
  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }

  // Getter for external access to files
  get filesList() {
    return this.files
  }

  get hasFiles() {
    return this.files.length > 0
  }
}